import React from 'react';

export const REQUEST_STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  // Server-side shortcut (request-crud.js TPRM_PIPELINE_STATUSES) for the two
  // statuses actively with a reviewer — the dashboard's TPRM tile links here.
  { value: 'pipeline', label: 'In the pipeline (submitted or under review)' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'under_review', label: 'Under review' },
  { value: 'changes_requested', label: 'Changes requested' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'withdrawn', label: 'Withdrawn' },
  { value: 'draft', label: 'Draft' },
];

export const RISK_FILTER_OPTIONS = [
  { value: '', label: 'All risks' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

const REQUEST_STATUS_TONES = {
  draft: 'gray',
  submitted: 'blue',
  under_review: 'yellow',
  approved: 'green',
  rejected: 'red',
  changes_requested: 'yellow',
  withdrawn: 'gray',
};

const REQUEST_STATUS_LABELS = {
  draft: 'Draft',
  submitted: 'Submitted',
  under_review: 'Under review',
  approved: 'Approved',
  rejected: 'Rejected',
  changes_requested: 'Changes requested',
  withdrawn: 'Withdrawn',
};

const REVIEW_TONES = {
  pending: 'gray',
  approved: 'green',
  rejected: 'red',
  changes_requested: 'yellow',
};

const REVIEW_LABELS = {
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
  changes_requested: 'Needs info',
};

const REVIEW_SIGNOFF_LABELS = {
  pending: 'Not signed off',
  approved: 'Signed off',
  rejected: 'Rejected',
  changes_requested: 'Needs info',
};

const FIRST_PASS_TONES = {
  not_started: 'gray',
  queued: 'blue',
  running: 'blue',
  complete: 'green',
  failed: 'red',
  stale: 'yellow',
};

const FIRST_PASS_LABELS = {
  not_started: 'Not started',
  queued: 'Queued',
  running: 'Running',
  complete: 'Complete',
  failed: 'Failed',
  stale: 'Stale',
};

function chipClass(tone, size) {
  return `chip ${size === 'small' ? 'sq ' : ''}${tone || 'gray'}`.trim();
}

function titleize(value) {
  const text = String(value || '').replace(/_/g, ' ');
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : '';
}

export function RequestStatusBadge({ status, size = 'default', showDot = true }) {
  const key = status || 'draft';
  return (
    <span className={chipClass(REQUEST_STATUS_TONES[key], size)}>
      {showDot && size !== 'small' && <span className="dot" />}
      {REQUEST_STATUS_LABELS[key] || titleize(key)}
    </span>
  );
}

export function ReviewStatusBadge({ status = 'pending', label, variant = 'default', showDot = true }) {
  const key = status || 'pending';
  const labels = variant === 'signoff' ? REVIEW_SIGNOFF_LABELS : REVIEW_LABELS;
  return (
    <span className={chipClass(REVIEW_TONES[key], 'default')}>
      {showDot && <span className="dot" />}
      {label ? `${label}: ` : ''}
      {labels[key] || titleize(key)}
    </span>
  );
}

export function FirstPassBadge({ status = 'not_started', showDot = true }) {
  const key = status || 'not_started';
  return (
    <span className={chipClass(FIRST_PASS_TONES[key], 'default')}>
      {showDot && <span className="dot" />}
      {FIRST_PASS_LABELS[key] || titleize(key)}
    </span>
  );
}

export function RiskRatingBadge({ rating, suffix = '', emptyLabel = '—', showDot = false, size = 'default' }) {
  if (!rating) {
    return (
      <span className={chipClass('gray', size)}>
        {showDot && <span className="dot" />}
        {emptyLabel}
      </span>
    );
  }
  const key = String(rating).toLowerCase();
  const tone = key === 'high' ? 'red' : key === 'medium' ? 'yellow' : 'green';
  return (
    <span className={chipClass(tone, size)}>
      {showDot && <span className="dot" />}
      {titleize(key)}{suffix}
    </span>
  );
}
